import { NOTE_W, laneCount, layout, nearestStep, stepX, stepY } from '../lib/board.js';
import * as store from '../lib/store.js';

const KEY = 'gp_wall_v3';

/** Onder deze afstand is het een klik en geen sleep. */
const SLEEP_DREMPEL = 4;

/** De zijbalk ligt links over de paal heen; daar moet een krabbel vandaan blijven. */
const ZIJBALK_TOT = 292;

/**
 * Lucht tussen de buitenste krabbel en de rand van het venster.
 *
 * Op de telefoon minder, en dat scheelt een hele baan. Met zestien aan weerskanten
 * viel een scherm van 360 net onder de grens voor twee banen en hing alles kaarsrecht
 * onder elkaar; met acht past de zigzag er wel in, en dan houdt de buitenste krabbel
 * daar nog tien pixels over tot de rand. Er staat op mobiel toch geen zijbalk waar
 * hij vandaan moet blijven.
 */
/**
 * Waar het hart van de paal staat, als deel van de breedte.
 *
 * De paal stond precies in het midden. Op het bureaublad staat hij iets naar rechts,
 * zodat er links plek is voor de programma's. Eén getal om aan te draaien, en de
 * vijf plekken die het midden nodig hebben halen het hier op: de paal zelf, elke
 * krabbel, het gestippelde doelvakje en het slepen. Stond het los opgeschreven, dan
 * schoof de paal wel op maar landden gesleepte krabbels ernaast.
 *
 * Op de telefoon blijft hij in het midden: daar staat er niets naast.
 */
const PAAL_DEEL = 0.56;

/**
 * De doos waar de krabbels in hangen wordt niet breder dan dit.
 *
 * Staat als `max-width` in Gallery.jsx; hier omdat de paal met dezelfde maat moet
 * rekenen. Draai je aan de een, draai dan ook aan de ander.
 */
const PAAL_VLAK_MAX = 1180;

const RAND = 16;
const RAND_MOBIEL = 8;

/**
 * Krabbels ophangen aan de paal.
 *
 * Je pakt een krabbel op en hangt hem in een ander vakje. Hangt daar al iemand, dan
 * ruilen ze van plek. Daardoor kan de paal nooit een stapel worden en hoef je zelf
 * nergens op te letten.
 *
 * Tijdens het slepen verschuift alleen de opgepakte krabbel, rechtstreeks op het
 * element. Pas bij loslaten volgt één hertekening, en dan zweven de betrokken
 * krabbels naar hun nieuwe vakje. Zonder dat zouden honderdtwintig krabbels bij elke
 * muisbeweging opnieuw getekend worden.
 *
 * Waar je een krabbel hangt onthoudt de browser, niet de server. Het is dus jouw
 * paal en niet die van iedereen; zie het rapport onder "De paal delen".
 */
export default {
  wallOverrides() {
      if (this.wallPos) return this.wallPos;
      let uit = {};
      try {
        const raw = store.get(KEY);
        const p = raw ? JSON.parse(raw) : null;
        // Alleen paren van twee hele getallen; de rest gooien we weg in plaats van
        // erop te vertrouwen.
        if (p && typeof p === 'object') {
          for (const id of Object.keys(p)) {
            const v = p[id];
            if (Number.isInteger(v) && v >= 0) uit[id] = v;
          }
        }
      } catch (e) {
        uit = {};
      }
      this.wallPos = uit;
      return uit;
    },

  saveWallOverrides() {
      try { store.set(KEY, JSON.stringify(this.wallPos || {})); } catch (e) { /* dan onthoudt hij het niet */ }
    },

  /**
   * Hoe breed het vlak is waar de krabbels op hangen.
   *
   * Dat vlak zit in een doos met `max-width: 1180` en `margin: 0 auto` (zie
   * Gallery.jsx), en daarbuiten zit de padding van de galerij. Boven de 1180 pixels
   * is dat vlak dus smaller dan het venster, en staat het in het midden.
   *
   * Twee dingen die je moet weten: het vlak is altijd op het midden van het venster
   * gecentreerd, en zijn breedte is niet de vensterbreedte. Precies daar ging het mis;
   * zie paalMiddenCss.
   */
  paalVlakBreed() {
      const vw = (typeof window === 'undefined' ? 1200 : window.innerWidth);
      const pad = this.state.isMobile ? 14 : 28;
      return Math.max(0, Math.min(PAAL_VLAK_MAX, vw - 2 * pad));
    },

  /**
   * Waar het hart van de paal staat, gerekend vanaf de linkerrand van het venster.
   *
   * Iets rechts van het midden, zodat er links plek is voor de programma's. Dat
   * "iets" is een deel van de breedte van het krabbelvlak en niet van het venster,
   * en daar zat de fout: de paal werd geplaatst met 56% van het vénster (1064 op
   * 1900) en de krabbels met 56% van hun vlak van 1180, dat op 360 begint (1021).
   * Boven de 1180 liepen die twee dus uit elkaar en hingen de krabbels drieënveertig
   * pixels naast de paal. Daaronder viel het samen, en daarom zag je het alleen op
   * een breed scherm.
   *
   * De zijbalk links ligt over de paal heen en duwt hem niet opzij; hij knijpt alleen
   * het aantal banen af. Zie wallReach.
   */
  wallCentre() {
      const vw = (typeof window === 'undefined' ? 1200 : window.innerWidth);
      if (this.state.isMobile) return vw * 0.5;
      return vw / 2 + (PAAL_DEEL - 0.5) * this.paalVlakBreed();
    },

  /**
   * Datzelfde hart als css-waarde, voor alles wat met `left` geplaatst wordt.
   *
   * Als afstand vanaf het midden en niet als percentage. Dat is de hele truc: de paal
   * hangt in een doos die zo breed is als het venster en de krabbels in een doos van
   * hoogstens 1180, maar die twee dozen hebben hetzelfde midden. `50%` wijst dus in
   * beide naar hetzelfde punt, en de pixels erachter ook.
   */
  paalMiddenCss() {
      if (this.state.isMobile) return '50%';
      // Afgerond, anders staat er calc(50% + 70.80000000000001px) in de css.
      const uit = Math.round((PAAL_DEEL - 0.5) * this.paalVlakBreed() * 10) / 10;
      return 'calc(50% + ' + uit + 'px)';
    },

  /**
   * Hoever een krabbel vanaf het hart van de paal mag komen zonder onder de zijbalk
   * of de cijferkolom te verdwijnen. Bepaalt hoeveel banen er passen.
   */
  wallReach() {
      const hart = this.wallCentre();
      const rand = this.state.isMobile ? RAND_MOBIEL : RAND;
      const rechterrand = window.innerWidth - rand;
      // Op mobiel staat er geen zijbalk over de paal heen, dus daar is de hele
      // linkerkant beschikbaar. Die er tóch van aftrekken hield maar één baan over
      // en zette alle krabbels kaarsrecht onder elkaar.
      const linkerrand = this.state.isMobile ? rand : ZIJBALK_TOT;
      return Math.max(NOTE_W / 2, Math.min(hart - linkerrand, rechterrand - hart));
    },

  wallLanes() {
      return laneCount(this.wallReach());
    },

  /** De plekken van alle krabbels, en hoe hoog het vlak moet worden. */
  wallLayout(items, isWide) {
      const uit = layout(items, isWide, this.wallOverrides(), this.wallLanes(), this.wallReach());
      this.wallSpots = uit.spots;
      return uit;
    },

  /** Heeft de gebruiker zelf iets verhangen? Dan mag de opruimknop verschijnen. */
  wallIsArranged() {
      return Object.keys(this.wallOverrides()).length > 0;
    },

  resetWall() {
      this.wallPos = {};
      this.saveWallOverrides();
      this.notify('De paal hangt weer zoals hij hing.');
      this.forceUpdate();
    },

  /**
   * Elke krabbel meldt zijn eigen div aan, zodat slepen hem direct kan verschuiven.
   *
   * Per krabbel één vaste functie, en die onthouden we. Een nieuwe functie per
   * hertekening zou React alle honderdtwintig verwijzingen laten loskoppelen en
   * opnieuw vastmaken, elke keer dat de chat iets ophaalt. Viel je muisklik net in
   * dat gaatje, dan vond het slepen geen element en gebeurde er niets.
   */
  noteRef(id) {
      if (!this.noteEls) this.noteEls = new Map();
      if (!this.noteRefs) this.noteRefs = new Map();
      let f = this.noteRefs.get(id);
      if (!f) {
        f = (el) => {
          if (el) this.noteEls.set(id, el);
          else this.noteEls.delete(id);
        };
        this.noteRefs.set(id, f);
      }
      return f;
    },

  onNoteDown(g, spot, e) {
      // Alleen de linkermuisknop, en niet op mobiel: daar is slepen niet te
      // onderscheiden van scrollen.
      if (e.button !== 0 || this.state.isMobile) return;
      const el = this.noteEls && this.noteEls.get(g.id);
      if (!el) return;
      e.preventDefault();
      this.dragNote = {
        id: g.id,
        startX: e.clientX,
        startY: e.clientY,
        vanStap: spot.step,
        origX: spot.x,
        origY: spot.y,
        lanes: this.wallLanes(),
        aantal: (this.state.galleryItems || []).length,
        el,
        // Wat React er zelf op had gezet, om het na afloop terug te zetten. Weghalen
        // werkt niet: React vergelijkt met zijn eigen vorige waarden en niet met de
        // pagina, dus een stijl die wij wisten schrijft hij nooit meer terug.
        zVoor: el.style.zIndex,
        cursorVoor: el.style.cursor,
        gesleept: false
      };
      window.addEventListener('mousemove', this.onNoteMove);
      window.addEventListener('mouseup', this.onNoteUp);
    },

  onNoteMove(e) {
      const d = this.dragNote;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.gesleept && Math.abs(dx) + Math.abs(dy) < SLEEP_DREMPEL) return;
      if (!d.gesleept) {
        d.gesleept = true;
        // Opgepakt: recht hangen, iets opwippen, en over de rest heen.
        d.el.classList.add('gp-tillen');
        d.el.style.zIndex = '30';
        d.el.style.cursor = 'grabbing';
      }
      // De krabbel volgt gewoon je muis. Dat klinkt vanzelfsprekend, maar hij
      // sprong eerst naar het dichtstbijzijnde vakje terwijl je nog aan het slepen
      // was, en dan schiet het ding overal heen behalve waar je hand is.
      const x = d.origX + dx;
      const y = d.origY + dy;
      // Ook onthouden, niet alleen op het element zetten. De app hertekent zichzelf
      // elke paar seconden omdat de chat en de tellers doorlopen, en zo'n
      // hertekening zette de krabbel anders midden in je sleep terug op zijn oude
      // plek. Hoe langer je sleepte, hoe groter die kans.
      d.x = x;
      d.y = y;
      d.el.style.left = 'calc(' + this.paalMiddenCss() + ' + ' + Math.round(x) + 'px)';
      d.el.style.top = Math.round(y) + 'px';

      // Waar hij terechtkomt laat het gestippelde vakje zien.
      const doel = nearestStep(x, y, d.lanes, d.aantal);
      d.doel = doel;
      this.toonDoelvakje(doel, d.lanes);
    },

  /**
   * Het gestippelde vakje dat laat zien waar de krabbel gaat hangen. Eén div die
   * we verplaatsen, geen hertekening: tijdens het slepen mag er niets zwaars
   * gebeuren.
   */
  toonDoelvakje(doel, lanes) {
      const el = this.dropRef && this.dropRef.current;
      if (!el) return;
      if (doel == null) { el.style.display = 'none'; return; }
      el.style.display = 'block';
      el.style.left = 'calc(' + this.paalMiddenCss() + ' + ' + stepX(doel, lanes) + 'px)';
      el.style.top = stepY(doel, lanes) + 'px';
    },

  onNoteUp() {
      const d = this.dragNote;
      this.dragNote = null;
      window.removeEventListener('mousemove', this.onNoteMove);
      window.removeEventListener('mouseup', this.onNoteUp);
      if (!d) return;
      d.el.style.cursor = d.cursorVoor || '';
      d.el.classList.remove('gp-tillen');
      // Terug naar wat React er had staan. Hier stond `removeProperty`, en dan had de
      // krabbel die je net verhing geen stapelhoogte meer en schoof hij onder zijn
      // buren tot je muis eraf ging.
      d.el.style.zIndex = d.zVoor || '';
      this.toonDoelvakje(null);

      // Let op de vergelijking: trede 0 bestaat, en `!0` is waar. Met `!d.doel`
      // stapte hij hier altijd uit zodra je naar de bovenste plek sleepte, en dan
      // gebeurde er niets. Alle andere treden werkten wel, dus het viel pas op als
      // je precies dat probeerde.
      //
      // Verder: niet gesleept betekent dat er niets van ons te wissen valt. `left`
      // en `top` weghalen zou de plek slopen die React er zelf op heeft gezet; de
      // krabbel valt dan naar de hoek van het vlak en glijdt daarna terug.
      // Eerst de klik opslokken die de browser na het loslaten nastuurt: ook een
      // krabbel die op zijn eigen plek of naast de paal terugkomt is gesleept, en
      // geen tik. Die klik opende anders de krabbel.
      if (d.gesleept) this.swallowNextClick();
      if (!d.gesleept || d.doel == null) return;

      // Loslaten: van waar je hem hebt losgelaten naar het vakje toe glijden. De
      // plek hier zetten en niet wissen, anders springt hij eerst terug naar zijn
      // oude plek voordat de hertekening hem naar de nieuwe stuurt.
      /**
       * Terug op je eigen plek: precies waar hij hing, met zijn zwabber en zijn klem.
       *
       * `stepX` en `stepY` zijn de kale plek van de trede, zonder de paar pixels scheef
       * die elke krabbel heeft en zonder de klem die een brede krabbel binnen de rand
       * houdt. Daar bleef hij dan staan, want voor React was er niets veranderd en die
       * schreef zijn eigen plek dus niet terug. `origX` en `origY` zijn die eigen plek.
       */
      if (d.doel === d.vanStap) {
        d.el.style.left = 'calc(' + this.paalMiddenCss() + ' + ' + Math.round(d.origX) + 'px)';
        d.el.style.top = Math.round(d.origY) + 'px';
        return;
      }
      d.el.style.left = 'calc(' + this.paalMiddenCss() + ' + ' + stepX(d.doel, d.lanes) + 'px)';
      d.el.style.top = stepY(d.doel, d.lanes) + 'px';

      this.hangOpTrede(d.id, d.doel, d.vanStap);
      this.forceUpdate();
    },

  /**
   * Hang een krabbel op een trede. Staat daar al iemand, dan gaat die naar de trede
   * die zojuist vrijkwam. Ruilen dus: een trede leegmaken zonder de ander ergens
   * heen te sturen zou hem onderaan de paal doen belanden.
   */
  hangOpTrede(id, naar, vandaan) {
      const plekken = this.wallOverrides();
      const bewoner = this.bewonerVan(naar, id);
      plekken[id] = naar;
      if (bewoner) plekken[bewoner] = vandaan;
      this.saveWallOverrides();
    },

  /** Wie staat er nu op deze trede? */
  bewonerVan(stap, behalve) {
      if (!this.wallSpots) return null;
      for (const [id, s] of this.wallSpots) {
        if (id !== behalve && s.step === stap) return id;
      }
      return null;
    },

  /**
   * Na het loslaten stuurt de browser nog een klik na. Die hoort de krabbel niet te
   * openen, want je was aan het slepen.
   *
   * Dit ging eerst met een vlaggetje, en dat was fout: bleef de klik uit, dan bleef
   * de vlag staan en slikte hij een half uur later een onschuldige klik in. Nu
   * luistert er eenmalig iemand mee, die zichzelf opruimt zodra hij zijn klik heeft
   * gehad of zodra duidelijk is dat er geen komt.
   */
  swallowNextClick() {
      const af = () => {
        window.removeEventListener('click', slik, true);
        clearTimeout(this.clickSwallowT);
      };
      const slik = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        af();
      };
      window.addEventListener('click', slik, true);
      clearTimeout(this.clickSwallowT);
      this.clickSwallowT = setTimeout(af, 400);
    },

};
